import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Theme store — binary light/dark mode switching.
 *
 * The theme is mirrored onto the `<html>` element as a `dark` class, matching
 * Kora's approach. A blocking script in `index.html` reads the persisted
 * value and applies the class BEFORE React mounts, so there's no flash of
 * the wrong theme on first paint.
 *
 * Defaults to `dark` because Merkflow is a dark-first product.
 *
 * Persist field name: `theme` (the previous iteration used `mode` — the boot
 * script in index.html reads both keys for backwards compat).
 */

export type ThemeName = 'light' | 'dark';

interface ThemeState {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  toggle: () => void;
}

/** Apply the theme imperatively to `<html class="dark">`. */
function applyTheme(theme: ThemeName) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
      toggle: () => {
        const next: ThemeName = get().theme === 'dark' ? 'light' : 'dark';
        applyTheme(next);
        set({ theme: next });
      },
    }),
    {
      name: 'merkflow-theme',
      storage: createJSONStorage(() => localStorage),
      // Migrate the legacy `mode` field to `theme` on hydration so users who
      // were on the previous schema don't get reset to dark on first load.
      migrate: (persistedState: unknown, _version): ThemeState => {
        const s = persistedState as Partial<ThemeState> & { mode?: ThemeName };
        return {
          theme: s.theme ?? s.mode ?? 'dark',
          setTheme: () => {},
          toggle: () => {},
        };
      },
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.theme);
      },
    },
  ),
);
